import { RadioButtonGroup } from "../../../components/Decision/RadioButtonGroup";
import { FormTextArea } from "../../../components/Input/FormTextArea";
import { CommonUserTaskNew } from "../CommonUserTaskNew";
import type { ComponentConfig } from "../types";
import { useState } from 'react';

import {
  Title,
  AccountNumberSection,
  AccountNumberCard,
  AccountNumberLabel,
  AccountNumberValue,
  AccountNumberList,
  AccountNumberItem,
  MatchIndicator,
  MismatchBanner,
  MismatchText,
  NoAccountText,
  PaddingContainer,
  CaseInfoContainer,
} from "./style";
import { WarningIcon } from "../../../components/Icons/WarningIcon";
import ErrorMessages from "../../../components/ErrorMessages/ErrorMessage";
import { ApplicantInfo } from "../../../components/Applicant/ApplicantInfo";
import { InfoRow } from "../../../components/InfoRow/InfoRow";

const ManualAccountNumberReview = (props: any) => {
  const { flow } = props;
  const loadedDecision = props?.task?.data?.decision || null;
  const loadedComment = props?.task?.data?.comment || "";

  // States
  const [decision, setDecision] = useState<boolean | null>(loadedDecision);
  const [comment, setComment] = useState<string>(loadedComment);
  const [errorMessages, setErrorMessages] = useState<string>("");

  const applicantInfo = flow?.data?.agentApiApplication?.applicant || {};
  const application = flow?.data?.agentApiApplication || {};

  const openBankingAccountNumbers = flow?.data?.integrations?.openBankingPSD2?.balance?.map((account: any) => account.accountId) || [];
  const confirmAccountNumberData = flow?.data?.integrations?.LeaBankWeb?.confirmAccountNumber;
  const confirmAccountNumber = typeof confirmAccountNumberData === 'object'
    ? confirmAccountNumberData?.accountNumber
    : confirmAccountNumberData;

  const onComplete = () => {
    if (decision === null) {
      setErrorMessages("Please select a decision");
      return;
    }
    const decisionText = decision === false ? "rejected" : "retry";
    const formData = {
      decision: decisionText,
      comment,
    };

    props.complete(
      formData,
      () => { },
      (e: any) => {
        console.log(e);
        setErrorMessages(e?.detail);
      },
    );
  }

  const onSave = () => {
    const formData = {
      decision,
      comment,
    };
    props.save(formData,
      () => console.log("success"),
      (e: any) => {
        console.log(e);
        setErrorMessages(e?.detail);
      },
    )
  }

  const hasMismatch = confirmAccountNumber &&
    openBankingAccountNumbers.length > 0 &&
    !openBankingAccountNumbers.includes(confirmAccountNumber);


  const formConfig: ComponentConfig[] = [
    {
      component: (
        <ApplicantInfo applicant={applicantInfo} />
      ),
      order: 1,
    },
    {
      component: (
        <PaddingContainer>
          <div style={{
            fontSize: "14px",
            fontWeight: "bold",
            marginBottom: "1rem",
            color: "#1e1e1e"
          }}>Case Details</div>
          <CaseInfoContainer>
            <InfoRow title="Applied loan amount" value={application.appliedAmount || '-'} hasMatch={false} hasPadding={false} />
            <InfoRow title="Email address" value={applicantInfo.emailAddress || '-'} hasMatch={false} hasPadding={false} />
            <InfoRow title="Phone number" value={applicantInfo.mobileNumber || '-'} hasMatch={false} hasPadding={false} />
          </CaseInfoContainer>
        </PaddingContainer>
      ),
      order: 2,
    },
    {
      component: (
        <RadioButtonGroup
          radioButtonValue={decision}
          onChange={(e: string) =>
            e === "true" ? setDecision(true) : setDecision(false)
          }
          title="Decision"
          options={[
            { value: true, label: "Retry confirm account number" },
            { value: false, label: "Reject application" },
          ]}
          id="manual-account-number-review"
        />
      ),
      order: 3,
    },
    {
      component: (
        <FormTextArea
          name="Comment"
          onChange={(e: string) => setComment(e)}
          label="Comment"
          defaultValue={comment || ""}
          placeholder="Other applicant information"
        />
      ),
      order: 4,
    },
    {
      component: <ErrorMessages errors={[errorMessages]} />,
      order: 5,
      hide: errorMessages === "",
    },
  ];

  const infoConfig = [
    {
      component: (
        <AccountNumberSection>
          <Title>Account Number Verification</Title>
          {hasMismatch && (
            <MismatchBanner>
              <WarningIcon />
              <MismatchText>
                The account number entered by the applicant does not match any account from open banking data
              </MismatchText>
            </MismatchBanner>
          )}
          <AccountNumberCard>
            <AccountNumberLabel>Entered by Applicant</AccountNumberLabel>
            {confirmAccountNumber ? (
              <AccountNumberValue>{confirmAccountNumber}</AccountNumberValue>
            ) : (
              <NoAccountText>No account number provided</NoAccountText>
            )}
          </AccountNumberCard>
          <AccountNumberCard>
            <AccountNumberLabel>Open Banking Accounts</AccountNumberLabel>
            {openBankingAccountNumbers.length > 0 ? (
              <AccountNumberList>
                {openBankingAccountNumbers.map((accountNumber: string) => {
                  const isMatch = accountNumber === confirmAccountNumber;
                  return (
                    <AccountNumberItem key={accountNumber} $isMatch={isMatch}>
                      <MatchIndicator $isMatch={isMatch}>
                        {isMatch ? '✓' : '−'}
                      </MatchIndicator>
                      <AccountNumberValue>{accountNumber}</AccountNumberValue>
                    </AccountNumberItem>
                  );
                })}
              </AccountNumberList>
            ) : (
              <NoAccountText>No accounts found in open banking data</NoAccountText>
            )}
          </AccountNumberCard>
        </AccountNumberSection>
      ),
      order: 1,
    },
  ];

  return (
    <CommonUserTaskNew
      handleComplete={onComplete}
      handleSave={onSave}
      infoConfig={infoConfig}
      formConfig={formConfig}
      overrideMasonryColumns={1}
    />
  );
};

export default ManualAccountNumberReview;
